import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Compass, Heart, Star, Target, Users, Briefcase, 
  Sparkles, Home, Leaf, Brain, Award, ChevronDown,
  CheckCircle2, TrendingUp
} from 'lucide-react';

// ACT Values Categories with examples
const VALUE_CATEGORIES = {
  relationships: {
    icon: <Users className="w-5 h-5" />,
    label: 'Relationships & Connection',
    colour: 'bg-secondary/20 border-secondary',
    values: [
      { name: 'Love', description: 'Giving and receiving care and affection' },
      { name: 'Intimacy', description: 'Being emotionally and physically close' },
      { name: 'Trust', description: 'Being reliable and depending on others' },
      { name: 'Friendship', description: 'Building and maintaining close bonds' },
      { name: 'Belonging', description: 'Being part of a community or group' },
      { name: 'Kindness', description: 'Showing care and compassion to others' },
      { name: 'Forgiveness', description: 'Letting go of resentment' },
      { name: 'Loyalty', description: 'Standing by those important to you' }
    ]
  },
  growth: {
    icon: <TrendingUp className="w-5 h-5" />,
    label: 'Personal Growth',
    colour: 'bg-accent/20 border-accent',
    values: [
      { name: 'Learning', description: 'Continuously acquiring knowledge' },
      { name: 'Self-awareness', description: 'Understanding yourself deeply' },
      { name: 'Challenge', description: 'Pushing beyond your comfort zone' },
      { name: 'Wisdom', description: 'Applying knowledge with discernment' },
      { name: 'Authenticity', description: 'Being true to yourself' },
      { name: 'Courage', description: 'Acting despite fear' },
      { name: 'Resilience', description: 'Bouncing back from adversity' },
      { name: 'Mindfulness', description: 'Being present and aware' }
    ]
  },
  health: {
    icon: <Heart className="w-5 h-5" />,
    label: 'Health & Wellbeing',
    colour: 'bg-destructive/20 border-destructive',
    values: [
      { name: 'Physical health', description: 'Caring for your body' },
      { name: 'Mental health', description: 'Nurturing your mind' },
      { name: 'Balance', description: 'Maintaining equilibrium in life' },
      { name: 'Self-care', description: 'Prioritising your needs' },
      { name: 'Vitality', description: 'Feeling alive and energised' },
      { name: 'Rest', description: 'Allowing recovery and renewal' },
      { name: 'Peace', description: 'Finding inner calm' },
      { name: 'Joy', description: 'Experiencing happiness and pleasure' }
    ]
  },
  work: {
    icon: <Briefcase className="w-5 h-5" />,
    label: 'Work & Achievement',
    colour: 'bg-primary/20 border-primary',
    values: [
      { name: 'Achievement', description: 'Accomplishing meaningful goals' },
      { name: 'Excellence', description: 'Doing your best work' },
      { name: 'Competence', description: 'Being skilled and capable' },
      { name: 'Success', description: 'Reaching important milestones' },
      { name: 'Contribution', description: 'Making a difference' },
      { name: 'Leadership', description: 'Guiding and inspiring others' },
      { name: 'Creativity', description: 'Expressing original ideas' },
      { name: 'Recognition', description: 'Being acknowledged for your work' }
    ]
  },
  meaning: {
    icon: <Compass className="w-5 h-5" />,
    label: 'Meaning & Purpose',
    colour: 'bg-secondary/20 border-secondary',
    values: [
      { name: 'Purpose', description: 'Having a sense of direction' },
      { name: 'Spirituality', description: 'Connecting to something greater' },
      { name: 'Service', description: 'Helping others and giving back' },
      { name: 'Legacy', description: 'Leaving a lasting impact' },
      { name: 'Gratitude', description: 'Appreciating what you have' },
      { name: 'Hope', description: 'Believing in positive possibilities' },
      { name: 'Faith', description: 'Trusting in life\'s unfolding' },
      { name: 'Wonder', description: 'Experiencing awe and curiosity' }
    ]
  },
  freedom: {
    icon: <Leaf className="w-5 h-5" />,
    label: 'Freedom & Autonomy',
    colour: 'bg-accent/20 border-accent',
    values: [
      { name: 'Independence', description: 'Making your own choices' },
      { name: 'Freedom', description: 'Living without constraint' },
      { name: 'Adventure', description: 'Seeking new experiences' },
      { name: 'Spontaneity', description: 'Being flexible and open' },
      { name: 'Privacy', description: 'Having personal space' },
      { name: 'Simplicity', description: 'Living without excess' },
      { name: 'Flexibility', description: 'Adapting to change' },
      { name: 'Exploration', description: 'Discovering new things' }
    ]
  },
  integrity: {
    icon: <Star className="w-5 h-5" />,
    label: 'Integrity & Character',
    colour: 'bg-muted/40 border-muted-foreground',
    values: [
      { name: 'Honesty', description: 'Being truthful and sincere' },
      { name: 'Integrity', description: 'Acting according to your principles' },
      { name: 'Fairness', description: 'Treating others equitably' },
      { name: 'Responsibility', description: 'Being accountable for your actions' },
      { name: 'Humility', description: 'Being modest and grounded' },
      { name: 'Patience', description: 'Accepting delays gracefully' },
      { name: 'Discipline', description: 'Maintaining self-control' },
      { name: 'Reliability', description: 'Being dependable' }
    ]
  }
};

interface SelectedValue {
  name: string;
  category: string;
  importance: number;
  currentAlignment: number;
  actionIdea?: string;
}

interface ValuesExplorerProps {
  onValuesChange?: (values: SelectedValue[]) => void;
  initialValues?: SelectedValue[];
  showAlignment?: boolean;
}

export function ValuesExplorer({ 
  onValuesChange, 
  initialValues = [],
  showAlignment = true 
}: ValuesExplorerProps) {
  const [selectedValues, setSelectedValues] = useState<SelectedValue[]>(initialValues);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState<string | null>(null);
  const [showSummary, setShowSummary] = useState(false);

  const toggleCategory = (category: string) => {
    setExpandedCategory(expandedCategory === category ? null : category);
  };

  const toggleValue = (valueName: string, category: string) => {
    const existing = selectedValues.find(v => v.name === valueName);
    let updated: SelectedValue[];
    
    if (existing) {
      updated = selectedValues.filter(v => v.name !== valueName);
    } else {
      updated = [...selectedValues, { 
        name: valueName, 
        category,
        importance: 8,
        currentAlignment: 5
      }];
    }
    
    setSelectedValues(updated);
    onValuesChange?.(updated);
  };

  const updateValue = (valueName: string, updates: Partial<SelectedValue>) => {
    const updated = selectedValues.map(v => 
      v.name === valueName ? { ...v, ...updates } : v
    );
    setSelectedValues(updated);
    onValuesChange?.(updated);
  };

  const isValueSelected = (valueName: string) => 
    selectedValues.some(v => v.name === valueName);

  const getValueData = (valueName: string) => 
    selectedValues.find(v => v.name === valueName);

  const topValues = selectedValues
    .sort((a, b) => b.importance - a.importance)
    .slice(0, 5);

  const alignmentGap = selectedValues.length > 0
    ? selectedValues.reduce((sum, v) => sum + (v.importance - v.currentAlignment), 0) / selectedValues.length
    : 0;

  return (
    <div className="space-y-6">
      {/* Introduction */}
      <Card className="bg-gradient-to-br from-primary/5 to-accent/5 border-primary/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Compass className="w-5 h-5 text-primary" />
            Values Clarification
          </CardTitle>
          <CardDescription>
            Values are directions we want to move in life, not destinations we arrive at. 
            Understanding your values helps you make choices aligned with who you want to be.
            Select values that resonate with you - there are no right or wrong answers.
          </CardDescription>
        </CardHeader>
        {selectedValues.length > 0 && (
          <CardContent>
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-accent" />
                <span>{selectedValues.length} values selected</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSummary(!showSummary)}
              >
                {showSummary ? 'Hide' : 'View'} Summary
              </Button>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Summary Panel */}
      <AnimatePresence>
        {showSummary && selectedValues.length > 0 && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
          >
            <Card className="bg-gradient-to-br from-secondary/5 to-primary/5 border-secondary/20">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Award className="w-5 h-5 text-secondary" />
                  Your Top Values
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Top 5 Values */}
                <div className="space-y-3">
                  {topValues.map((value, index) => (
                    <div key={value.name} className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-secondary/20 flex items-center justify-center text-xs font-bold">
                        {index + 1}
                      </span>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium">{value.name}</span>
                          {showAlignment && (
                            <span className="text-xs text-muted-foreground">
                              Living at {value.currentAlignment}/10
                            </span>
                          )}
                        </div>
                        {showAlignment && (
                          <Progress 
                            value={value.currentAlignment * 10} 
                            className="h-2"
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Alignment Gap */}
                {showAlignment && alignmentGap > 2 && (
                  <div className="p-3 rounded-lg bg-secondary/10 border border-secondary/20">
                    <p className="text-sm">
                      <strong>Values-Action Gap:</strong> There's a gap between what matters to you 
                      and how you're currently living. This isn't a criticism - it's information 
                      that can guide small, meaningful changes.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Value Categories */}
      <div className="space-y-3">
        {Object.entries(VALUE_CATEGORIES).map(([key, category]) => {
          const isExpanded = expandedCategory === key;
          const selectedInCategory = selectedValues.filter(v => v.category === key);
          
          return (
            <Card 
              key={key} 
              className={`transition-all ${isExpanded ? category.colour : 'hover:border-primary/30'}`}
            >
              <CardHeader 
                className="pb-2 cursor-pointer"
                onClick={() => toggleCategory(key)}
              >
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    {category.icon}
                    {category.label}
                    {selectedInCategory.length > 0 && (
                      <Badge variant="secondary" className="ml-2">
                        {selectedInCategory.length}
                      </Badge>
                    )}
                  </CardTitle>
                  <ChevronDown className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                </div>
              </CardHeader>
              
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                  >
                    <CardContent className="pt-0">
                      <div className="flex flex-wrap gap-2 mb-4">
                        {category.values.map(value => {
                          const isSelected = isValueSelected(value.name);
                          const valueData = getValueData(value.name);
                          
                          return (
                            <div key={value.name} className="relative">
                              <Badge
                                variant={isSelected ? 'default' : 'outline'}
                                className="cursor-pointer transition-all hover:scale-105"
                                onClick={() => toggleValue(value.name, key)}
                              >
                                {value.name}
                                {isSelected && (
                                  <button
                                    className="ml-1 hover:text-primary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingValue(editingValue === value.name ? null : value.name);
                                    }}
                                  >
                                    ✎
                                  </button>
                                )}
                              </Badge>
                              
                              {/* Edit Panel */}
                              <AnimatePresence>
                                {editingValue === value.name && valueData && (
                                  <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    className="absolute z-20 mt-1 p-3 rounded-lg bg-card border shadow-lg min-w-[250px]"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <div className="space-y-3">
                                      <div>
                                        <label className="text-xs font-medium">
                                          Importance: {valueData.importance}/10
                                        </label>
                                        <input
                                          type="range"
                                          min="1"
                                          max="10"
                                          value={valueData.importance}
                                          onChange={(e) => updateValue(value.name, { importance: parseInt(e.target.value) })}
                                          className="w-full mt-1"
                                        />
                                      </div>
                                      {showAlignment && (
                                        <div>
                                          <label className="text-xs font-medium">
                                            Currently living: {valueData.currentAlignment}/10
                                          </label>
                                          <input
                                            type="range"
                                            min="1"
                                            max="10"
                                            value={valueData.currentAlignment}
                                            onChange={(e) => updateValue(value.name, { currentAlignment: parseInt(e.target.value) })}
                                            className="w-full mt-1"
                                          />
                                        </div>
                                      )}
                                      <div>
                                        <label className="text-xs font-medium">Small action idea</label>
                                        <Textarea
                                          placeholder="One tiny step to live this value more..."
                                          value={valueData.actionIdea || ''}
                                          onChange={(e) => updateValue(value.name, { actionIdea: e.target.value })}
                                          className="mt-1 text-xs min-h-[60px]"
                                        />
                                      </div>
                                      <Button 
                                        size="sm" 
                                        variant="outline"
                                        onClick={() => setEditingValue(null)}
                                      >
                                        Done
                                      </Button>
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Value descriptions */}
                      <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                        {category.values.map(value => (
                          <div key={value.name} className="flex items-start gap-1">
                            <span className="font-medium text-foreground">{value.name}:</span>
                            <span>{value.description}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </motion.div>
                )}
              </AnimatePresence>
            </Card>
          );
        })}
      </div>

      {/* Reflection Prompt */}
      {selectedValues.length >= 3 && (
        <Card className="border-primary/20">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-primary shrink-0" />
              <div>
                <h4 className="font-semibold mb-1">Reflection</h4>
                <p className="text-sm text-muted-foreground">
                  With your top values identified, consider: What small action could you take 
                  this week that would move you toward one of these values? Remember, 
                  values-based living is about direction, not perfection.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export { VALUE_CATEGORIES };
